import { createBrowserHistory as createHistory } from 'history';
import { HOST } from '../backend/api-config';

export const history = createHistory();

export const hostname = window && window.location && window.location.hostname;

export const getCookie = (name) => {
	var cookieValue = null;
	if (document.cookie && document.cookie !== '') {
		var cookies = document.cookie.split(';');
		for (let i = 0; i < cookies.length; i++) {
			let cookie = cookies[i].trim();
			// Does this cookie string begin with the name we want?
			if (cookie.substring(0, name.length + 1) === name + '=') {
				cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
				break;
			}
		}
	}
	return cookieValue;
};

export const setCookie = (name, value, days) => {
	var expires = '';
	if (days) {
		var date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		expires = '; expires=' + date.toUTCString();
	}
	document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

export const USER_TYPES = {
	SELLER: 'seller',
	SELLER_ADMIN: 'seller_admin',
	LAB_SELLER: 'labvendor',
};

export const checkLabSeller = (userTypes = []) => {
	return userTypes.includes(USER_TYPES.LAB_SELLER);
};

export const checkSellerAdmin = (userTypes = []) => {
	return userTypes.includes(USER_TYPES.SELLER_ADMIN);
};

export const STOCK_STATUS = {
	IN_STOCK: 'In Stock',
	OUT_OF_STOCK: 'Out Of Stock',
	BACKORDER: 'Backorder',
};

export const validateURL = (url) => {
	if (typeof url !== 'string') {
		return false;
	}

	const URLRegex = new RegExp(/https?:\/\/[^\s]+\.[^\s]{2,}(\/[^\s?#]*)?(\?[^\s#]*)?(#[^\s]*)?/gi);
	return URLRegex.test(url);
};

export const validateSupplierProductId = (id) => {
	return !/[_:]/.test(id);
};

export const validateSearchText = (searchText) => {
	let searchTextRegex = new RegExp('^[a-zA-Z0-9- ]+$');
	return searchTextRegex.test(searchText);
};

export const validateKeyword = (keyword) => {
	let keywordRegex = new RegExp('^[a-zA-Z0-9-]+$');
	return keywordRegex.test(keyword);
};

export const GROUP_IDS = {
	'1300_DENTAL': '62ac252a38fa214d26c828f2',
	MAVEN_DENTAL: '63c8dfc7044ae30007e25ed7',
	LUMINO_DENTAL: '645cc544fdb76acfb6b35935',
};

export const REGION = {
	USA: 'US',
	AUSTRALIA: 'AU',
	NEW_ZEALAND: 'NZ',
	CANADA: 'CA',
	UNITED_KINGDOM: 'UK',
	FRANCE: 'FR',
	QA: 'QA',
};

export const isAUorNZ = () => [REGION.AUSTRALIA, REGION.NEW_ZEALAND, REGION.QA].includes(HOST);

export const isSpecialMarketRequired = (supplier) => ['HS', 'HSAU', 'HSCA'].includes(supplier);

export const isTaxableRequired = () => isAUorNZ();

export const isSubCategoryRequired = () => isAUorNZ();

export const isSubSubCategoryRequired = () => isAUorNZ();

export const isGLCodeRequired = (group_id) => [GROUP_IDS['1300_DENTAL'], GROUP_IDS['MAVEN_DENTAL'], GROUP_IDS['LUMINO_DENTAL']].includes(group_id);

export const hideDSOInfo = () => isAUorNZ();

export const formatNumber = (value) => {
	return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatCurrency = (value, addDecimals = false, isAlloy = false) => {
	
	if (value === null ) {
		return null;
	}

	value = String(value);
	if (value === '') {
		return null;
	} else {
		let decimalPosition = value.indexOf('.');
		if (decimalPosition >= 0) {
			let leftSide = value.substring(0, decimalPosition);
			let rightSide = value.substring(decimalPosition + 1);

			leftSide = formatNumber(leftSide);
			rightSide = formatNumber(rightSide.substring(0, 3));

			if (addDecimals) {
				leftSide = stripCurrency(leftSide);
				rightSide += '000';
			}
			rightSide = rightSide.substring(0, 3);
			value = `${leftSide ? leftSide : 0}.${rightSide}`;
		} else {
			value = formatNumber(value);
			value = `${value}`;
			if (addDecimals) value = `${value}.000`;
		}
		return `${CURRENCY_SYMBOL}${value}${isAlloy ? '/gram' : ''}`;
	}
};

export const stripCurrency = (value) => {
	if (value === null) return null;

	value = String(value);
	if (value === '') return null;

	const escapedSymbol = CURRENCY_SYMBOL.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
	const regex = new RegExp(escapedSymbol, 'g');

	value = value.replace(regex, '').replace(/,/g, '');
	value = parseFloat(value);
	return value;
};

export const managePricingLogic = (value, valueForApproval) => {
	if (valueForApproval !== null && valueForApproval !== undefined) {
		return formatCurrency(valueForApproval, true);
	} else if (value !== null && value !== undefined) {
		return formatCurrency(value, true);
	} else {
		return null;
	}
};

export const getNoOfBucketColumns = (rowRef, columnRef, fixedWidthInRem) => {
	let rowWidth = rowRef?.current?.offsetWidth || 0;
	let columnWidth = columnRef?.current?.offsetWidth || 1;

	let pixelsInOneRem = parseFloat(getComputedStyle(document.documentElement).fontSize);
	let fixedWidthInPixels = fixedWidthInRem * pixelsInOneRem;

	return Math.floor((rowWidth - fixedWidthInPixels) / columnWidth) - 1;
};

export const abbreviateName = (name) => {
	let abbreviation = '';
	name.split(' ').forEach((word, index) => {
		if (word !== '' && abbreviation.length < 2) {
			abbreviation = `${abbreviation}${word[0].toUpperCase()}`;
		}
	});
	return abbreviation;
};

export const SPJ_ACTIONS = {
	LAB_INVENTORY_CREATE: 'lab-inventory-create',
	LAB_INVENTORY_BULK_UPDATE: 'lab-inventory-bulk-update',
	LAB_SKU_PRICE_UPDATE: 'lab-sku-price-update',
	INVENTORY_CREATE: 'inventory-create',
	INVENTORY_UPDATE: 'inventory-update',
	PRICE_UPDATE: 'multiple-price-update',
	SALES_ORDER_UPDATE: 'sales-order-lines-update',
};

export const SPJ_STATUS = {
	FAILED: 'FAILED',
	PENDING: 'PENDING',
};

export const BULK_FILE_UPLOAD_STAGES = {
	UPLOAD: 'UPLOAD',
	PROCESSING: 'PROCESSING',
	SUCCESS: 'SUCCESS',
	ERROR: 'ERROR',
	FILE_ERRORS: 'FILE_ERRORS',
};

export const getOrderMonth = (month) => {
	switch (month + 1) {
		case 1:
			return 'January';
		case 2:
			return 'February';
		case 3:
			return 'March';
		case 4:
			return 'April';
		case 5:
			return 'May';
		case 6:
			return 'June';
		case 7:
			return 'July';
		case 8:
			return 'August';
		case 9:
			return 'September';
		case 10:
			return 'October';
		case 11:
			return 'November';
		case 12:
			return 'December';
		default:
			return '';
	}
};

export const SHIPPING_COMPANIES = {
	FED_EX: 'https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=',
	UPS: 'https://www.ups.com/WebTracking/processInputRequest?tracknum=',
	ON_TRAC: 'https://www.ontrac.com/trackingresults.asp?tracking_number=',
	USPS: 'https://tools.usps.com/go/TrackConfirmAction?tRef=fullpage&tLc=4&text28777=&tLabels=',
	DHL: 'https://www.dhl.com/en/express/tracking.html?brand=DHL&AWB=',
	UDS: 'https://www.uniteddeliveryservice.com/track/api/orderref/',
	DPD: 'https://track.dpdlocal.co.uk/search?reference=',
	ROYAL_MAIL: "https://www.royalmail.com/track-your-item?trackNumber="
};

export const generateShippingUrl = (company, item) => {
	return `${SHIPPING_COMPANIES[company]}${item}`;
};

export const getRestrictedFields = (user = {}, supplier = {}) => {
	const userType = user?.user_type || [];
	const userSellerPortalConfig = user?.sellerPortalConfig || {};
	const supplierSellerPortalConfig = supplier?.sellerPortalConfig || {};

	let restrictedFields = [];

	if (userSellerPortalConfig.hasOwnProperty('restricted_fields')) {
		restrictedFields = userSellerPortalConfig?.restricted_fields;
	} else if (supplierSellerPortalConfig.hasOwnProperty('restricted_fields')) {
		if (userType.includes('seller_admin')) {
			restrictedFields = supplierSellerPortalConfig?.restricted_fields?.admin;
		} else {
			restrictedFields = supplierSellerPortalConfig?.restricted_fields?.user;
		}
	}

	return restrictedFields || [];
};

export const joinProcedures = (procedures) => {
	return procedures.map((procedure) => `${procedure?.label || ''}*${procedure?.value || ''}`).join('||');
};

export const splitProcedures = (procedures) => {
	return procedures.split('||').map((procedure) => {
		procedure = procedure.split('*');
		return { label: procedure?.[0], value: procedure?.[1] };
	});
};

export const getDateTime24HoursBack = () => {
	let today = new Date();
	today.setDate(today.getDate() - 1);

	return today;
};

export const CURRENCY_SYMBOL = hostname === 'uk-seller.dentira.com' ? '£' : hostname === 'fr-seller.dentira.com' ? '€' : '$';
